import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, FileText, Bell, Server, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function PrivacyPolicyPage() {
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="bg-gray-50 min-h-screen py-8 sm:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8">
          <Link to="/">
            <Button variant="ghost" className="flex items-center text-gray-600">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour à l'accueil
            </Button>
          </Link>
        </div>

        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="p-4 sm:p-6 md:p-10">
            <div className="flex items-center mb-4 sm:mb-6">
              <Lock className="h-6 w-6 sm:h-8 sm:w-8 text-primary mr-2 sm:mr-3 flex-shrink-0" />
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Privacy Policy</h1>
            </div>
            
            <div className="text-xs sm:text-sm text-gray-500 mb-6 sm:mb-8">
              Effective Date: {today}
            </div>

            <div className="prose max-w-none text-sm sm:text-base">
              <p className="text-gray-700">
                At AEM Marketplace, accessible at <a href="https://aemfamily.com" className="text-primary hover:underline">https://aemfamily.com</a>, we value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and protect your data when you use our mobile application and website.
              </p>

              <div className="mt-6 sm:mt-8 space-y-6 sm:space-y-8">
                <section>
                  <div className="flex items-center mb-3 sm:mb-4">
                    <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-primary mr-2 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">1. Information We Collect</h2>
                  </div>
                  <p className="text-gray-700">We may collect the following information:</p>
                  <ul className="list-disc pl-5 sm:pl-6 mt-2 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Your name and contact details (if you create an account or contact us)</li>
                    <li>Purchase and order history</li>
                    <li>Device and browser information (for performance and analytics)</li>
                    <li>Payment information (via secure third-party services like PayPal)</li>
                  </ul>
                  <p className="text-gray-700 mt-2">We do not collect sensitive personal data.</p>
                </section>

                <section>
                  <div className="flex items-center mb-3 sm:mb-4">
                    <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-primary mr-2 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">2. How We Use Your Information</h2>
                  </div>
                  <p className="text-gray-700">We use the data to:</p>
                  <ul className="list-disc pl-5 sm:pl-6 mt-2 space-y-1 sm:space-y-2 text-gray-700">
                    <li>Provide access to the marketplace and its features</li>
                    <li>Process orders and communicate with users and sellers</li>
                    <li>Improve our services and user experience</li>
                    <li>Prevent fraud or misuse of the platform</li>
                  </ul>
                </section>

                <section>
                  <div className="flex items-center mb-3 sm:mb-4">
                    <Server className="h-4 w-4 sm:h-5 sm:w-5 text-primary mr-2 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">3. Third-Party Services</h2>
                  </div>
                  <p className="text-gray-700">Our app and website may use trusted third-party services like:</p>
                  <ul className="list-disc pl-5 sm:pl-6 mt-2 space-y-1 sm:space-y-2 text-gray-700">
                    <li>PayPal for secure payments</li>
                    <li>Google Analytics or similar tools for anonymous analytics</li>
                  </ul>
                  <p className="text-gray-700 mt-2">These services have their own privacy policies and comply with global data protection standards.</p>
                </section>

                <section>
                  <div className="flex items-center mb-3 sm:mb-4">
                    <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-primary mr-2 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">4. Data Security</h2>
                  </div>
                  <p className="text-gray-700">
                    We take reasonable steps to protect your data from unauthorized access, misuse, or disclosure. However, no method is 100% secure.
                  </p>
                </section>

                <section>
                  <div className="flex items-center mb-3 sm:mb-4">
                    <HelpCircle className="h-4 w-4 sm:h-5 sm:w-5 text-primary mr-2 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">5. Your Rights</h2>
                  </div>
                  <p className="text-gray-700">
                    You can request to access, update, or delete your personal information by contacting us at:
                  </p>
                  <p className="mt-2 text-gray-700">
                    Email: <a href="mailto:aemfamily509@gmail.com" className="text-primary hover:underline">aemfamily509@gmail.com</a>
                  </p>
                </section>

                <section>
                  <div className="flex items-center mb-3 sm:mb-4">
                    <Bell className="h-4 w-4 sm:h-5 sm:w-5 text-primary mr-2 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">6. Changes to This Policy</h2>
                  </div>
                  <p className="text-gray-700">
                    We may update this Privacy Policy from time to time. All changes will be posted on this page.
                  </p>
                </section>

                <section>
                  <div className="flex items-center mb-3 sm:mb-4">
                    <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-primary mr-2 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">7. Contact Us</h2>
                  </div>
                  <p className="text-gray-700">
                    If you have any questions about this Privacy Policy, contact us at:
                  </p>
                  <div className="mt-2 text-gray-700">
                    <p>AEM Family Support</p>
                    <p>Email: <a href="mailto:aemfamily509@gmail.com" className="text-primary hover:underline">aemfamily509@gmail.com</a></p>
                    <p>Website: <a href="https://aemfamily.com" className="text-primary hover:underline">https://aemfamily.com</a></p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}